/**
 * Детерминированный визуал категории.
 *
 * Категория не хранит картинку: цвет и глиф выводятся из имени. Одинаковое
 * имя всегда даёт одинаковый визуал на всех устройствах, без сети и файлов.
 */

export interface CategoryGradient {
  /** Начальный цвет градиента. */
  from: string
  /** Конечный цвет градиента. */
  to: string
}

/**
 * 24 пастельных градиента: мягкие, не утомляют глаза при долгой смене.
 * Насыщенность достаточная, чтобы белая иконка читалась (плюс лёгкая тень
 * в `CategoryGlyph`), и цвета различимы в тёмной теме.
 */
export const CATEGORY_PALETTE: CategoryGradient[] = [
  { from: '#F7B2B2', to: '#E08A8A' },
  { from: '#F7C9A3', to: '#E0A878' },
  { from: '#F7E0A3', to: '#E0C478' },
  { from: '#D9EFA8', to: '#B8D67F' },
  { from: '#B8E6C1', to: '#8FD3A0' },
  { from: '#A8E6D5', to: '#7FD3BE' },
  { from: '#A6E3EA', to: '#7FCFD9' },
  { from: '#B3D9F5', to: '#8CC3E8' },
  { from: '#C0C8F5', to: '#9AA6E8' },
  { from: '#D3C2F2', to: '#B39DE0' },
  { from: '#E8C2F2', to: '#D19DE0' },
  { from: '#F5C2D6', to: '#E89DBB' },
  { from: '#F7D1A3', to: '#E0B078' },
  { from: '#DCF2B3', to: '#C2DE8C' },
  { from: '#B3F2E0', to: '#8CDFC8' },
  { from: '#B3E8F2', to: '#8CD1E0' },
  { from: '#B3D6F5', to: '#8CB8E8' },
  { from: '#D6C2F5', to: '#B89DE8' },
  { from: '#E8C2F5', to: '#D19DE8' },
  { from: '#F5C2D1', to: '#E89DB3' },
  { from: '#F7D9B3', to: '#E0C28C' },
  { from: '#C2E8C8', to: '#9DD3A6' },
  { from: '#C2F2F5', to: '#9DE0E8' },
  { from: '#D1C2F5', to: '#B39DE8' }
]

/** Количество минималистичных глифов в наборе. */
export const CATEGORY_GLYPH_COUNT = 16

export interface CategoryVisual {
  /** Индекс градиента в палитре. */
  colorKey: number
  /** Индекс глифа. */
  glyph: number
}

/** FNV-1a: стабильный 32-битный хеш строки. */
export function hashString(value: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

/**
 * Визуал категории. Ручные `colorKey`/`glyph` имеют приоритет над хешем.
 */
export function categoryVisual(
  name: string,
  colorKey?: number | null,
  glyph?: number | null
): CategoryVisual {
  const hash = hashString(name.trim().toLowerCase())
  return {
    colorKey:
      colorKey != null && colorKey >= 0
        ? colorKey % CATEGORY_PALETTE.length
        : hash % CATEGORY_PALETTE.length,
    glyph:
      glyph != null && glyph >= 0
        ? glyph % CATEGORY_GLYPH_COUNT
        : Math.floor(hash / CATEGORY_PALETTE.length) % CATEGORY_GLYPH_COUNT
  }
}

/** Градиент категории (для плиток товаров и фонов). */
export function categoryGradient(
  name: string,
  colorKey?: number | null
): CategoryGradient {
  return CATEGORY_PALETTE[categoryVisual(name, colorKey).colorKey]
}
